import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Paper,
  Container,
  Snackbar
} from '@mui/material';
import AdminLayout from '../../components/AdminLayout';
import { patientsService } from '../../services/firebaseServices';
import jsPDF from 'jspdf';

const CertificadosViaje = () => {
  const [pacientes, setPacientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPaciente, setSelectedPaciente] = useState(null);
  const [certificadoData, setCertificadoData] = useState({
    // Datos del doctor
    nombreDoctor: 'Dr. Belkys Hernandez',
    matriculaDoctor: '12345678',
    // Datos de la mascota (se llenan automáticamente)
    nombreMascota: '',
    especieMascota: '',
    razaMascota: '',
    edadMascota: '',
    sexoMascota: '',
    pesoMascota: '',
    // Datos del propietario (se llenan automáticamente)
    nombrePropietario: '',
    cedulaPropietario: '',
    telefonoPropietario: '',
    // Vacunas y desparacitación
    vacunas: [],
    desparacitaciones: [],
    // Direcciones
    direccionPartida: '',
    direccionDestino: '',
    // Información adicional
    fechaCertificado: new Date().toISOString().split('T')[0],
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Cargar pacientes al montar
  const cargarPacientes = useCallback(async () => {
    try {
      setLoading(true);
      const resultado = await patientsService.getAllPatients();
      if (resultado.success) {
        setPacientes(resultado.data);
      }
    } catch (error) {
      console.error('Error cargando pacientes:', error);
      mostrarSnackbar('Error al cargar pacientes', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarPacientes();
  }, [cargarPacientes]);

  const handleSelectPaciente = (pacienteId) => {
    const paciente = pacientes.find(p => p.id === pacienteId);
    if (paciente) {
      setSelectedPaciente(paciente);
      
      // Obtener edad con múltiples fallbacks
      let edadMascota = '';
      if (paciente.edad) {
        edadMascota = paciente.edad;
      } else if (paciente.edadCalculada) {
        edadMascota = paciente.edadCalculada;
      } else if (paciente.fechaNacimiento) {
        // Calcular edad desde fecha de nacimiento si está disponible
        try {
          let fechaNac;
          if (paciente.fechaNacimiento && typeof paciente.fechaNacimiento === 'object' && paciente.fechaNacimiento.seconds) {
            fechaNac = new Date(paciente.fechaNacimiento.seconds * 1000);
          } else if (typeof paciente.fechaNacimiento === 'string') {
            fechaNac = new Date(paciente.fechaNacimiento);
          } else {
            fechaNac = paciente.fechaNacimiento;
          }
          
          if (fechaNac && !isNaN(fechaNac.getTime())) {
            const hoy = new Date();
            let años = hoy.getFullYear() - fechaNac.getFullYear();
            const mesCumple = fechaNac.getMonth();
            const diaCumple = fechaNac.getDate();
            const mesActual = hoy.getMonth();
            const diaActual = hoy.getDate();
            
            const noCumplioEsteAno = mesActual < mesCumple || (mesActual === mesCumple && diaActual < diaCumple);
            if (noCumplioEsteAno) {
              años--;
            }
            edadMascota = `${años} años`;
          }
        } catch (error) {
          console.warn('Error calculando edad:', error);
          edadMascota = '';
        }
      }
      
      // Actualizar datos del certificado con datos del paciente
      setCertificadoData(prev => ({
        ...prev,
        nombreMascota: paciente.nombre || '',
        especieMascota: paciente.especie || '',
        razaMascota: paciente.raza || '',
        edadMascota: edadMascota,
        sexoMascota: paciente.sexo || '',
        pesoMascota: paciente.peso || '',
        nombrePropietario: `${paciente.propietario || ''} ${paciente.propietarioApellido || ''}`.trim(),
        cedulaPropietario: paciente.propietarioCedula || paciente.cedula || '',
        telefonoPropietario: paciente.propietarioTelefono || paciente.telefono || '',
        vacunas: Array.isArray(paciente.vacunas) ? paciente.vacunas : (paciente.vacunas ? [paciente.vacunas] : []),
      }));
    }
  };

  const handleCambioDesparacitacion = (index, campo, valor) => {
    const nuevasDesparacitaciones = [...certificadoData.desparacitaciones];
    if (!nuevasDesparacitaciones[index]) {
      nuevasDesparacitaciones[index] = {};
    }
    nuevasDesparacitaciones[index] = {
      ...nuevasDesparacitaciones[index],
      [campo]: valor
    };
    setCertificadoData(prev => ({
      ...prev,
      desparacitaciones: nuevasDesparacitaciones
    }));
  };

  const handleCambioVacuna = (index, campo, valor) => {
    const nuevasVacunas = [...certificadoData.vacunas];
    if (typeof nuevasVacunas[index] === 'string') {
      nuevasVacunas[index] = { nombre: nuevasVacunas[index] };
    }
    nuevasVacunas[index] = {
      ...nuevasVacunas[index],
      [campo]: valor
    };
    setCertificadoData(prev => ({
      ...prev,
      vacunas: nuevasVacunas
    }));
  };

  const mostrarSnackbar = (mensaje, severity = 'success') => {
    setSnackbar({
      open: true,
      message: mensaje,
      severity
    });
  };

  const generarPDF = () => {
    if (!selectedPaciente) {
      mostrarSnackbar('Por favor selecciona un paciente', 'warning');
      return;
    }

    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      let yPosition = 15;

      // Header con logo
      const logoUrl = '/logo.png';
      const img = new Image();
      img.src = logoUrl;
      
      // Agregar logo (lado izquierdo)
      try {
        doc.addImage(logoUrl, 'PNG', 15, 10, 25, 25);
      } catch (e) {
        console.warn('No se pudo cargar el logo:', e);
      }
      
      // Título al lado del logo
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.text('CERTIFICADO DE VIAJE', pageWidth / 2 + 10, yPosition + 5, { align: 'center' });
      
      yPosition += 25;
      
      // Línea divisoria
      yPosition += 8;
      doc.setDrawColor(0);
      doc.line(15, yPosition, pageWidth - 15, yPosition);
      yPosition += 8;

      // Párrafo combinado de información
      doc.setFont(undefined, 'normal');
      doc.setFontSize(11);
      
      const parrafo = `Yo ${certificadoData.nombreDoctor} con matrícula profesional No. ${certificadoData.matriculaDoctor}, Médica Veterinaria, certifico que la mascota de especie ${certificadoData.especieMascota}, con nombre ${certificadoData.nombreMascota}, de raza ${certificadoData.razaMascota}, de sexo ${certificadoData.sexoMascota}, con edad ${certificadoData.edadMascota}, peso de ${certificadoData.pesoMascota} Kg, propiedad de ${certificadoData.nombrePropietario} identificada C.C ${certificadoData.cedulaPropietario}. Teléfono: ${certificadoData.telefonoPropietario}.`;
      
      doc.text(parrafo, 15, yPosition, { align: 'justify', maxWidth: pageWidth - 30 });
      const parrafoWrapped = doc.splitTextToSize(parrafo, pageWidth - 30);
      yPosition += parrafoWrapped.length * 5 + 5;

      // Segundo párrafo - Estado de salud
      const parrafo2 = `Se encuentra en óptimo estado de salud y en condiciones físicas adecuadas para viajar a cualquier destino nacional como internacional. No presenta síntomas de enfermedades infectocontagiosa en el momento de la valoración médica veterinaria. Se encuentra libre del parásito (Cochliomyia hominivorax) conocido como gusano barrenador. Esquema vacunal completo como consta en el carnet. Está al día en el control de parásitos externos e internos.`;
      
      doc.text(parrafo2, 15, yPosition, { align: 'justify', maxWidth: pageWidth - 30 });
      const parrafo2Wrapped = doc.splitTextToSize(parrafo2, pageWidth - 30);
      yPosition += parrafo2Wrapped.length * 5 + 3;

      // Vacunas
      yPosition += 3;
      doc.setFont(undefined, 'bold');
      doc.setFontSize(11);
      doc.text('PLAN DE VACUNAS:', 15, yPosition);
      yPosition += 6;
      doc.setFont(undefined, 'normal');
      doc.setFontSize(9);

      if (certificadoData.vacunas && certificadoData.vacunas.length > 0) {
        certificadoData.vacunas.forEach((vacuna, idx) => {
          const nombreVacuna = typeof vacuna === 'string' ? vacuna : (vacuna.nombre || 'Sin nombre');
          const loteVacuna = vacuna.lote || 'Sin lote';
          const fechaVacuna = vacuna.fecha || 'Sin fecha';
          const textoVacuna = `${idx + 1}. ${nombreVacuna} | Lote: ${loteVacuna} | Fecha: ${fechaVacuna}`;
          doc.text(textoVacuna, 20, yPosition);
          yPosition += 5;
        });
      } else {
        doc.text('No hay vacunas registradas', 20, yPosition);
        yPosition += 5;
      }

      // Desparacitación
      yPosition += 5;
      doc.setFont(undefined, 'bold');
      doc.setFontSize(11);
      doc.text('PLAN DE DESPARACITACIÓN:', 15, yPosition);
      yPosition += 6;
      doc.setFont(undefined, 'normal');
      doc.setFontSize(9);

      if (certificadoData.desparacitaciones && certificadoData.desparacitaciones.length > 0) {
        certificadoData.desparacitaciones.forEach((desparasitacion, idx) => {
          const descripcion = desparasitacion.descripcion || 'Sin descripción';
          const lote = desparasitacion.lote || 'Sin lote';
          const registro = desparasitacion.registro || 'Sin registro';
          const principioActivo = desparasitacion.principioActivo || 'Sin especificar';
          
          // Línea 1: Descripción
          doc.text(`${idx + 1}. ${descripcion}`, 20, yPosition);
          yPosition += 5;
          // Línea 2: Lote y Registro
          doc.text(`   Lote: ${lote} | Registro: ${registro}`, 20, yPosition);
          yPosition += 5;
          // Línea 3: Principio Activo
          doc.text(`   Principio Activo: ${principioActivo}`, 20, yPosition);
          yPosition += 7;
        });
      } else {
        doc.text('No hay desparacitaciones registradas', 20, yPosition);
        yPosition += 5;
      }

      // Dirección de partida y llegada
      yPosition += 5;
      doc.setFont(undefined, 'bold');
      doc.setFontSize(10);
      doc.text('DIRECCIÓN DE PARTIDA:', 15, yPosition);
      yPosition += 5;
      doc.setFont(undefined, 'normal');
      const direccionPartidaWrapped = doc.splitTextToSize(certificadoData.direccionPartida, pageWidth - 30);
      doc.text(direccionPartidaWrapped, 15, yPosition);
      yPosition += direccionPartidaWrapped.length * 5 + 2;

      doc.setFont(undefined, 'bold');
      doc.text('DIRECCIÓN DE LLEGADA:', 15, yPosition);
      yPosition += 5;
      doc.setFont(undefined, 'normal');
      const direccionDestinoWrapped = doc.splitTextToSize(certificadoData.direccionDestino, pageWidth - 30);
      doc.text(direccionDestinoWrapped, 15, yPosition);
      yPosition += direccionDestinoWrapped.length * 5 + 3;

      // Lugar y fecha de expedición
      doc.text(`Este certificado fue expedido en Popayán, ${certificadoData.fechaCertificado}`, 15, yPosition);
      yPosition += 8;

      // Firma
      yPosition += 15;
      doc.line(15, yPosition, 75, yPosition);
      yPosition += 5;
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.text(certificadoData.nombreDoctor, 15, yPosition);
      yPosition += 5;
      doc.setFont(undefined, 'normal');
      doc.text('Médico Veterinario', 15, yPosition);
      yPosition += 5;
      doc.text(`M.P. ${certificadoData.matriculaDoctor}`, 15, yPosition);

      // Guardar PDF
      doc.save(`Certificado_Viaje_${certificadoData.nombreMascota}_${new Date().getTime()}.pdf`);
      mostrarSnackbar('Certificado generado exitosamente', 'success');
    } catch (error) {
      console.error('Error generando PDF:', error);
      mostrarSnackbar('Error al generar el certificado', 'error');
    }
  };

  return (
    <AdminLayout title="Certificados de Viaje">
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
          ✈️ Certificados de Viaje para Mascotas
        </Typography>

        <Grid container spacing={3}>
        {/* Panel de Selección de Paciente */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Seleccionar Paciente
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Paciente</InputLabel>
                <Select
                  value={selectedPaciente?.id || ''}
                  onChange={(e) => handleSelectPaciente(e.target.value)}
                  disabled={loading}
                >
                  <MenuItem value="">-- Selecciona un paciente --</MenuItem>
                  {pacientes.map(paciente => (
                    <MenuItem key={paciente.id} value={paciente.id}>
                      {paciente.nombre} ({paciente.especie})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {loading && <CircularProgress />}
            </CardContent>
          </Card>
        </Grid>

        {/* Panel de Información del Doctor */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Información del Médico Veterinario
              </Typography>
              
              <TextField
                fullWidth
                label="Nombre del Doctor"
                value={certificadoData.nombreDoctor}
                onChange={(e) => setCertificadoData(prev => ({ ...prev, nombreDoctor: e.target.value }))}
                sx={{ mb: 2 }}
              />
              
              <TextField
                fullWidth
                label="Matrícula Profesional (M.P.)"
                value={certificadoData.matriculaDoctor}
                onChange={(e) => setCertificadoData(prev => ({ ...prev, matriculaDoctor: e.target.value }))}
                sx={{ mb: 2 }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Panel de Información de la Mascota */}
        {selectedPaciente && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Información de la Mascota
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      label="Nombre"
                      value={certificadoData.nombreMascota}
                      onChange={(e) => setCertificadoData(prev => ({ ...prev, nombreMascota: e.target.value }))}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      label="Especie"
                      value={certificadoData.especieMascota}
                      onChange={(e) => setCertificadoData(prev => ({ ...prev, especieMascota: e.target.value }))}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      label="Raza"
                      value={certificadoData.razaMascota}
                      onChange={(e) => setCertificadoData(prev => ({ ...prev, razaMascota: e.target.value }))}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      label="Edad"
                      value={certificadoData.edadMascota}
                      onChange={(e) => setCertificadoData(prev => ({ ...prev, edadMascota: e.target.value }))}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      label="Sexo"
                      value={certificadoData.sexoMascota}
                      onChange={(e) => setCertificadoData(prev => ({ ...prev, sexoMascota: e.target.value }))}
                      size="small"
                      placeholder="Ej: Macho, Hembra"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      label="Peso (Kg)"
                      type="number"
                      value={certificadoData.pesoMascota}
                      onChange={(e) => setCertificadoData(prev => ({ ...prev, pesoMascota: e.target.value }))}
                      size="small"
                      placeholder="Ej: 25.5"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Panel de Información del Propietario */}
        {selectedPaciente && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Información del Propietario
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      label="Nombre"
                      value={certificadoData.nombrePropietario}
                      onChange={(e) => setCertificadoData(prev => ({ ...prev, nombrePropietario: e.target.value }))}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      label="Cédula"
                      value={certificadoData.cedulaPropietario}
                      onChange={(e) => setCertificadoData(prev => ({ ...prev, cedulaPropietario: e.target.value }))}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      label="Teléfono"
                      value={certificadoData.telefonoPropietario}
                      onChange={(e) => setCertificadoData(prev => ({ ...prev, telefonoPropietario: e.target.value }))}
                      size="small"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Panel de Vacunas */}
        {selectedPaciente && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Vacunas Aplicadas
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => {
                      setCertificadoData(prev => ({
                        ...prev,
                        vacunas: [...prev.vacunas, { nombre: '', lote: '', fecha: '' }]
                      }));
                    }}
                  >
                    + Agregar Vacuna
                  </Button>
                </Box>
                
                {certificadoData.vacunas && certificadoData.vacunas.length > 0 ? (
                  <Box sx={{ maxHeight: '500px', overflowY: 'auto' }}>
                    {certificadoData.vacunas.map((vacuna, idx) => (
                      <Paper key={idx} sx={{ p: 2, mb: 2, backgroundColor: '#f5f5f5' }}>
                        <Grid container spacing={2} alignItems="flex-start">
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              fullWidth
                              label="Nombre de la Vacuna"
                              size="small"
                              value={typeof vacuna === 'string' ? vacuna : (vacuna.nombre || '')}
                              onChange={(e) => handleCambioVacuna(idx, 'nombre', e.target.value)}
                              placeholder="ej: Rabia, Parvovirus, etc."
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              fullWidth
                              label="Lote"
                              size="small"
                              value={vacuna.lote || ''}
                              onChange={(e) => handleCambioVacuna(idx, 'lote', e.target.value)}
                              placeholder="Número de lote"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              type="date"
                              size="small"
                              label="Fecha de Aplicación"
                              value={vacuna.fecha || ''}
                              onChange={(e) => handleCambioVacuna(idx, 'fecha', e.target.value)}
                              InputLabelProps={{ shrink: true }}
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              fullWidth
                              onClick={() => {
                                const nuevasVacunas = certificadoData.vacunas.filter((_, i) => i !== idx);
                                setCertificadoData(prev => ({
                                  ...prev,
                                  vacunas: nuevasVacunas
                                }));
                              }}
                            >
                              Eliminar
                            </Button>
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                  </Box>
                ) : (
                  <Alert severity="info">No hay vacunas registradas. Haz clic en "Agregar Vacuna" para añadir una.</Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Panel de Desparacitación */}
        {selectedPaciente && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Desparacitación
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => {
                      setCertificadoData(prev => ({
                        ...prev,
                        desparacitaciones: [...prev.desparacitaciones, { descripcion: '', lote: '', registro: '', principioActivo: '' }]
                      }));
                    }}
                  >
                    + Agregar Desparacitación
                  </Button>
                </Box>

                <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {certificadoData.desparacitaciones && certificadoData.desparacitaciones.length > 0 ? (
                    certificadoData.desparacitaciones.map((desparacitacion, idx) => (
                      <Paper key={idx} sx={{ p: 2, mb: 2, backgroundColor: '#f5f5f5' }}>
                        <Grid container spacing={2}>
                          {/* Fila 1: Descripción y Lote */}
                          <Grid item xs={12} sm={8} md={9}>
                            <TextField
                              label="Descripción"
                              size="small"
                              fullWidth
                              value={desparacitacion.descripcion || ''}
                              onChange={(e) => handleCambioDesparacitacion(idx, 'descripcion', e.target.value)}
                              placeholder="Ej: Control de parásitos internos y externos con Advocate pipeta de 0.8 ml. Solución tópica"
                            />
                          </Grid>
                          <Grid item xs={12} sm={4} md={3}>
                            <TextField
                              label="Lote"
                              size="small"
                              fullWidth
                              value={desparacitacion.lote || ''}
                              onChange={(e) => handleCambioDesparacitacion(idx, 'lote', e.target.value)}
                              placeholder="Número de lote"
                            />
                          </Grid>
                          {/* Fila 2: Registro y Principio Activo */}
                          <Grid item xs={12} sm={4} md={3}>
                            <TextField
                              label="Registro"
                              size="small"
                              fullWidth
                              value={desparacitacion.registro || ''}
                              onChange={(e) => handleCambioDesparacitacion(idx, 'registro', e.target.value)}
                              placeholder="Ej: ICA 1645"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={7}>
                            <TextField
                              label="Principio Activo"
                              size="small"
                              fullWidth
                              value={desparacitacion.principioActivo || ''}
                              onChange={(e) => handleCambioDesparacitacion(idx, 'principioActivo', e.target.value)}
                              placeholder="Ej: Imidacloprid 100mg, Moxidectina 25mg"
                            />
                          </Grid>
                          <Grid item xs={12} sm={2} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              fullWidth
                              onClick={() => {
                                const nuevasDesparacitaciones = certificadoData.desparacitaciones.filter((_, i) => i !== idx);
                                setCertificadoData(prev => ({
                                  ...prev,
                                  desparacitaciones: nuevasDesparacitaciones
                                }));
                              }}
                            >
                              Eliminar
                            </Button>
                          </Grid>
                        </Grid>
                      </Paper>
                    ))
                  ) : (
                    <Alert severity="info">No hay desparacitaciones registradas. Haz clic en "Agregar Desparacitación" para añadir una.</Alert>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Panel de Información de Viaje */}
        {selectedPaciente && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Información de Viaje
                </Typography>
                
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Dirección de Partida"
                  value={certificadoData.direccionPartida}
                  onChange={(e) => setCertificadoData(prev => ({ ...prev, direccionPartida: e.target.value }))}
                  sx={{ mb: 2 }}
                />
                
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Dirección de Destino"
                  value={certificadoData.direccionDestino}
                  onChange={(e) => setCertificadoData(prev => ({ ...prev, direccionDestino: e.target.value }))}
                />
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Botones de Acción */}
        {selectedPaciente && (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={generarPDF}
              >
                📄 Generar PDF
              </Button>
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        message={snackbar.message}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Container>
    </AdminLayout>
  );
};

export default CertificadosViaje;
